import React, { useEffect, useMemo, useState } from 'react';
import { ComponentTypeNames } from '../../schema';
import { DatoCmsLink } from '../../schema/datocms/blocks';
import { DatoCmsBannerAlert } from '../../schema/datocms/blocks/banner-alert';
import { ButtonStyles } from '../../schema/enum/button-styles';
import { Theme, Themes } from '../../schema/enum/themes';
import Button from '../button/button';
import Icon from '../icon/icon';
import StructuredText from '../structured-text/structured-text';
import { useThemeClassMap } from '../theme/useThemeClassMap';

export interface BannerAlertProps {
    bannerAlert: DatoCmsBannerAlert;
}

const BannerAlert = ({ bannerAlert }: BannerAlertProps): React.ReactElement => {
    const { theme, title, message, links } = bannerAlert;

    /*
       The banner may briefly change to a password reset banner based on URL parameters. React needs
       to be told to re-render this component on the client to fully replace all properties (e.g. className),
       otherwise it will only try to replace inner text on elements. https://reactjs.org/docs/react-dom.html#hydrate
    */
    const [isClient, setIsClient] = useState<boolean>(false);

    const themeClass = useThemeClassMap(theme);

    const iconName = useMemo(() => getIconNameFromTheme(theme), [theme]);

    useEffect(() => {
        setIsClient(true);
    }, []);

    let button: DatoCmsLink | undefined = undefined;
    if (links && links.length > 0) {
        button = {
            ...links[0],
            __typename: ComponentTypeNames.LINK,
            styleOverride: ButtonStyles.BORDERED
        };
    }

    function getIconNameFromTheme(theme: Theme): string {
        switch(theme) {
            case Themes.DANGER:
                return 'report';
            case Themes.WARNING:
                return 'warning';
            case Themes.INFO:
                return 'info';
            case Themes.SUCCESS:
                return 'check_circle';
            default:
                return 'info';
        }
    }

    return (
        <div className={`${themeClass} bg-theme-primary-alt border-b-8 border-theme-primary p-3`} key={`${theme}-${isClient}`}>
            <div className="flex items-center flex-wrap lg:flex-nowrap container mx-auto">
                <Icon name={iconName} className="hidden lg:block text-theme-primary fill-current h-16 w-16 mr-5" />
                <div className="flex flex-col lg:w-7/12">
                    <div className="flex items-center">
                        <Icon name={iconName} className="lg:hidden text-theme-primary fill-current h-10 w-10 mr-2" />
                        <span className="text-theme-primary-text font-bold">{title}</span>
                    </div>
                    <StructuredText data={message} className="banner-alert__message text-sm"/>
                </div>


                { button && <Button className="text-sm lg:ml-auto lg:mr-0 mt-2 lg:mt-0" {...button} /> }
            </div>
        </div>
    );
};

export default BannerAlert;
